import axios, { AxiosResponse } from 'axios';
import { APIResponse } from '../Helper/type';
import IUserLogin from './UserLogin.interface';
import {
  handleApiResponseGetSsoUrl,
  handleApiResponseLogin,
  handleApiResponseSSOLogin
} from '../Helper/apiResponseHandler';
import apiCallWrapper from '../../apiwrapper/apiCallWrapper';
import { store } from '../../store/store';
import { loginUser } from '../../store/slice/sessionSlice';
import { responseMessages } from '../Helper/enums';
import { encrypt } from '../Helper/encryption';

// Declaring the web server path
const backEndServerUrl = String(process.env.REACT_APP_BACK_END_SERVER_URL ?? '');
export default class UserLogin {
  public apiResponse: APIResponse | undefined;

  // Declaring the constructor to get the request body. This constructor is of type IUser
  constructor(public loginInfo: IUserLogin) {}

  // Declaring the sub-path
  public loginPath: string = '/login';
  public authenticateUserPath: string = '/authenticateUser';
  public resendUrlPath: string = '/resendurl';
  public oauthToken: string = '/oauth/token';
  public mfaSetupVerificationPath: string = '/mfaSetupVerification';
  public oauthLogin: string = '/oauth/url';

  // POST request for Login and storing it to db. This methods returns a response body along with a status code.
  public async doLogin(otp: string): Promise<APIResponse> {
    try {
      // calling wrapper function to wrap the header.
      const axiosWithRoles = apiCallWrapper(this.loginPath, 'POST');
      const url: string = this.createUri(this.loginPath);
      const requestBody: any = this.loginInfo;
      requestBody.otp = otp;
      const response: AxiosResponse<any, any> = await axiosWithRoles.post(url, requestBody);
      // Flag to determine whether the response is for different API or not
      const isUserLoggedIn = false;
      // storing jwt token and role so that we can use it for further api calls.
      const jwtToken = response.data.cognitoDetails.message.idToken.jwtToken;
      const preferredRoleArn =
        response.data.cognitoDetails.message.idToken.payload['cognito:preferred_role'];
      const preferredRole = preferredRoleArn.split('/').pop();
      const groupName = response.data.cognitoDetails.message.idToken.payload['cognito:groups'][0];
      // storing groupname to show on the user page.
      sessionStorage.setItem('groupName', groupName);
      sessionStorage.setItem('preferredRole', preferredRole);
      const encryptedToken = encrypt(jwtToken);
      sessionStorage.setItem('jwtToken', encryptedToken);
      store.dispatch(loginUser());
      this.apiResponse = handleApiResponseLogin(response, isUserLoggedIn);
    } catch (error: any) {
      switch (error?.response?.status) {
        case 301:
          this.apiResponse = { statusCode: 301, body: JSON.stringify(error) };
          break;
        case 403:
          this.apiResponse = {
            statusCode: 403,
            body: 'User is not verified'
          };
          break;
        case 423:
          this.apiResponse = {
            statusCode: 423,
            body: 'Account is locked, please contact BT Admin'
          };
          break;
        case 429:
          this.apiResponse = {
            statusCode: 429,
            body: 'Account is temporarily Locked, Please try after 10 mins'
          };
          break;
        case 401:
          this.apiResponse = {
            statusCode: 401,
            body: 'invalid credentials.'
          };
          break;
        case 410:
          this.apiResponse = {
            statusCode: 410,
            body: 'OTP mismatched.'
          };
          break;
        default:
          this.apiResponse = { statusCode: 500, body: JSON.stringify(error) };
      }
    }
    return this.apiResponse;
  }

  public async userMfaSetup(otp: string, accessToken: string): Promise<APIResponse> {
    try {
      // calling wrapper function to wrap the header.
      const axiosWithRoles = apiCallWrapper(this.mfaSetupVerificationPath, 'POST');
      const url: string = this.createUri(this.mfaSetupVerificationPath);
      const requestBody: any = this.loginInfo;
      requestBody.otp = otp;
      requestBody.accessToken = accessToken;
      const response: AxiosResponse<any, any> = await axiosWithRoles.post(url, requestBody);
      // Flag to determine whether the
      this.apiResponse = {
        statusCode: 201,
        body: 'MFA authentication successful.'
      };
    } catch (error: any) {
      switch (error?.response?.status) {
        case 403:
          this.apiResponse = {
            statusCode: 403,
            body: 'Invalid User ID or Password'
          };
          break;
        default:
          this.apiResponse = { statusCode: 500, body: JSON.stringify(error) };
      }
    }
    return this.apiResponse;
  }

  // POST request for Resending URL while user is not confirmed for Admin and storing it to db. This methods returns a response body along with a status code.
  public async resendUrl(): Promise<APIResponse> {
    try {
      const axiosWithRoles = apiCallWrapper(this.resendUrlPath, 'POST');
      const url: string = this.createUri(this.resendUrlPath);
      const requestBody: any = this.loginInfo;
      const response: AxiosResponse<any, any> = await axiosWithRoles.post(url, requestBody);
      // Flag to determine whether the response is for different API or not
      const isUserLoggedIn = true;
      this.apiResponse = handleApiResponseLogin(response, isUserLoggedIn);
    } catch (error: any) {
      this.apiResponse = {
        statusCode: 500,
        body: JSON.stringify(error)
      };
    }
    return this.apiResponse;
  }

  // Send POST request to exchange Cognito auth code for JWT token
  public async ssoLogin(code: string): Promise<APIResponse> {
    try {
      const axiosWithRoles = apiCallWrapper(this.oauthToken, 'POST');
      const url: string = this.createUri(this.oauthToken);
      const requestBody: any = {
        code
      };
      // Request to exchange auth code with a token
      const response: AxiosResponse<any, any> = await axiosWithRoles.post(url, requestBody);
      const jwtToken = response.data.token;
      const preferredRole = response.data.payload['cognito:preferred_role'].split('/').pop();
      const groupName = response.data.payload['cognito:groups'][0];
      const email = response.data.payload.email;
      // Store groupName, jwtToken, preferredRole, and userEmail for session management, API access, and UI display
      const encryptedToken = encrypt(jwtToken);
      sessionStorage.setItem('groupName', groupName);
      sessionStorage.setItem('jwtToken', encryptedToken);
      sessionStorage.setItem('preferredRole', preferredRole);
      sessionStorage.setItem('userEmail', email);
      store.dispatch(loginUser());
      this.apiResponse = handleApiResponseSSOLogin(response);
    } catch (error: unknown) {
      console.log(error);
      if (axios.isAxiosError(error) && error.response) {
        const response = error.response;
        this.apiResponse = handleApiResponseSSOLogin(response);
      } else {
        return { statusCode: 500, body: responseMessages.SomethingWentWrong };
      }
    }
    return this.apiResponse;
  }

  // Fetch the configured SSO endpoint for user login flow
  public async getSsoLoginUrl(): Promise<APIResponse> {
    try {
      const axiosWithRoles = apiCallWrapper(this.oauthLogin, 'GET');
      const url: string = this.createUri(this.oauthLogin);
      const response: AxiosResponse<any, any> = await axiosWithRoles.get(url);
      this.apiResponse = handleApiResponseGetSsoUrl(response);
    } catch (error: any) {
      console.error(error);
      if (axios.isAxiosError(error) && error.response) {
        const response = error.response;
        this.apiResponse = handleApiResponseGetSsoUrl(response);
      }
      return { statusCode: 500, body: responseMessages.SomethingWentWrong };
    }
    return this.apiResponse;
  }

  private createUri(paramsResendUrl?: string): string {
    return backEndServerUrl + paramsResendUrl;
  }
}
